<template>
    <div>
        <b-card no-body class="w-100 mb-4 mt-3 pl-0">
            <template #header>
                <b-row class="w-100 no-gutters">
                    <b-col md="6">
                        <div class="property-title-container">
                            <span v-if="techInfo.property.thumbnail != null"
                                ><img class="property-thumbnail" :src="techInfo.property.thumbnail"
                            /></span>
                            <span class="property-name">
                                <b>{{ techInfo.property.name }}</b>
                            </span>
                        </div>
                    </b-col>
                    <b-col md="6" v-if="!isCancelled" class="reservation-deposit-container">
                        <span class="float-right reservation-deposit">
                            Reservation Deposit =
                            <span class="edit-link-container">
                                <a href="javascript:void(0)" @click="showChangeReservationDepositModal()">{{
                                    getReservationDepositValue()
                                }}</a
                                >&nbsp;<font-awesome-icon
                                    color="#0D47A1"
                                    :icon="['fas', 'pen-to-square']"
                                ></font-awesome-icon>
                            </span>
                            <span v-if="!techInfo.deposit_is_flat"> = {{ techInfo.payment_deposit }}</span>
                        </span>
                    </b-col>
                </b-row>
            </template>
            <b-card-body v-if="!isCancelled">
                <b-row class="no-gutters">
                    <b-col md="12">
                        <services-table
                            ref="services"
                            v-model="techInfo.chosen_services"
                            :free="techInfo.free_services"
                            :reservation-id="techInfo.id"
                            :meta-reservation-id="metaReservationId"
                            class="reservation-invoice-grid"
                        ></services-table>
                    </b-col>
                </b-row>
                <b-row class="no-gutters" v-if="!metaReservation.tax_exemption">
                    <b-col md="12">
                        <taxes-table
                            ref="taxes"
                            v-model="techInfo.reservation_taxes"
                            :free="techInfo.free_taxes"
                            :reservation-id="techInfo.id"
                            :meta-reservation-id="metaReservationId"
                            class="reservation-invoice-grid"
                        ></taxes-table>
                    </b-col>
                </b-row>
                <b-row class="no-gutters">
                    <b-col md="12">
                        <deposits-table
                            ref="deposits"
                            v-model="techInfo.deposit_payments"
                            :reservation-id="techInfo.id"
                            :meta-reservation-id="metaReservationId"
                            class="reservation-invoice-grid"
                        ></deposits-table>
                    </b-col>
                </b-row>
                <b-row class="no-gutters">
                    <b-col md="12">
                        <discounts-table
                            ref="discounts"
                            v-model="techInfo.reservation_discounts"
                            :free="techInfo.free_discounts"
                            :reservation-id="techInfo.id"
                            :meta-reservation-id="metaReservationId"
                            class="reservation-invoice-grid"
                        ></discounts-table>
                    </b-col>
                </b-row>
                <b-row class="no-gutters">
                    <b-col md="12">
                        <fees-table
                            ref="fees"
                            v-model="techInfo.additional_fees"
                            :free="techInfo.free_fees"
                            :tax-options="feeTaxOptions"
                            :deposit-options="feeDepositOptions"
                            :reservation-id="techInfo.id"
                            :meta-reservation-id="metaReservationId"
                            class="reservation-invoice-grid"
                        ></fees-table>
                    </b-col>
                </b-row>
            </b-card-body>
        </b-card>
        <validation-observer ref="form" v-slot="{ meta, setFieldValue }" as="span">
            <modal
                :id="changeReservationDepositModalId"
                title="Change Reservation Deposit"
                size="sm"
                :ok-disabled="!meta.valid"
                @ok.prevent="changeReservationDepositValue"
            >
                <b-row class="no-gutters">
                    <b-col md="5">
                        <validation-provider name="amount" v-slot="{ errors }" rules="validFloat|required">
                            <form-input
                                floating-label="Amount"
                                v-model="reservationDepositAmount"
                                :error="errors[0]"
                                class="mr-1"
                                @input="(val) => setFieldValue('amount', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="7">
                        <form-select
                            floating-label="Type"
                            v-model="reservationDepositIsFlat"
                            :options="reservationDepositIsFlatOptions"
                            class="ml-1"
                        ></form-select>
                    </b-col>
                </b-row>
            </modal>
        </validation-observer>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { Decimal } from 'decimal.js';
    import API from '../../../api';
    import { invoiceMixin } from '../mixins/invoice-mixin';
    import Modal from '../../generic/modal/modal.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import ServicesTable from './tech-info-tables/services-table.vue';
    import TaxesTable from './tech-info-tables/taxes-table.vue';
    import DepositsTable from './tech-info-tables/deposits-table.vue';
    import DiscountsTable from './tech-info-tables/discounts-table.vue';
    import FeesTable from './tech-info-tables/fees-table.vue';
    import { RESERVATION_TECHRESERVATION_CHANGE_PAYMENT_DEPOSIT_ENDPOINT } from '../../../endpoints';

    export default {
        name: 'TechInfoForm',
        components: {
            BusyModal,
            ServicesTable,
            TaxesTable,
            DepositsTable,
            DiscountsTable,
            FeesTable,
            Modal,
            FormInput,
            FormSelect,
        },
        mixins: [invoiceMixin],
        props: {
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        computed: {
            ...mapGetters('header', ['userToday', 'userUnicodeDateFormat', 'userCurrencySymbol']),
            feeTaxOptions() {
                return this.techInfo.reservation_taxes
                    .filter(function (obj) {
                        return !obj.is_flat;
                    })
                    .map((item) => ({
                        value: item.id,
                        text: item.title,
                    }));
            },
            feeDepositOptions() {
                return [
                    {
                        value: null,
                        text: 'Rental Charges',
                    },
                ].concat(
                    this.techInfo.deposit_payments.map((item) => ({
                        value: item.id,
                        text: item.title,
                    }))
                );
            },
            isCancelled() {
                return this.metaReservation.reservation_status.state === 'CANCELLED';
            },
        },
        data() {
            return {
                loading: false,
                techInfo: this.initialValues.tech_info,
                reservationId: this.initialValues.reservation_id,
                metaReservationId: this.initialValues.meta_reservation_id,
                metaReservation: this.initialValues.meta_reservation,
                changeReservationDepositModalId: `change-reservation-deposit-modal-${this.initialValues.tech_info.id}`,
                reservationDepositAmount: 0,
                reservationDepositIsFlat: false,
                reservationDepositIsFlatOptions: [
                    { text: 'Percentage %', value: false },
                    { text: 'Fixed', value: true },
                ],
            };
        },
        methods: {
            getInitialValues() {
                return this.initialValues;
            },
            getReservationDepositValue() {
                if (this.techInfo.deposit_is_flat) {
                    return (
                        this.userCurrencySymbol +
                        Decimal(this.techInfo.payment_deposit_value).toFixed(2, Decimal.ROUND_HALF_EVEN)
                    );
                } else {
                    return (
                        Decimal(this.techInfo.reservation_deposit_combined).toFixed(2, Decimal.ROUND_HALF_EVEN) + '%'
                    );
                }
            },
            showChangeReservationDepositModal() {
                if (this.techInfo.deposit_is_flat) {
                    this.reservationDepositAmount = Decimal(this.techInfo.payment_deposit_value).toFixed(
                        2,
                        Decimal.ROUND_HALF_EVEN
                    );
                } else {
                    this.reservationDepositAmount = Decimal(this.techInfo.reservation_deposit_combined).toFixed(
                        2,
                        Decimal.ROUND_HALF_EVEN
                    );
                }
                this.reservationDepositIsFlat = this.techInfo.deposit_is_flat;
                this.$refs.form.setValues({
                    amount: this.reservationDepositAmount,
                    commission_calculation: this.reservationDepositIsFlat,
                });
                this.$bvModal.show(this.changeReservationDepositModalId);
            },
            async changeReservationDepositValue(event) {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_TECHRESERVATION_CHANGE_PAYMENT_DEPOSIT_ENDPOINT(this.techInfo.id), {
                        value: this.reservationDepositAmount,
                        deposit_is_flat: this.reservationDepositIsFlat,
                    });
                    await this.refreshReservationInvoice(this.metaReservationId);
                    this.$bvModal.hide(this.changeReservationDepositModalId);
                } catch (e) {
                    this.errorToast({ text: `Could not set reservation deposit.` });
                } finally {
                    this.busy = false;
                }
            },
        },
        watch: {
            initialValues() {
                this.metaReservation = this.initialValues.meta_reservation;
                this.reservationId = this.initialValues.reservation_id;
                this.metaReservationId = this.initialValues.meta_reservation_id;
                this.techInfo = this.initialValues.tech_info;
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .reservation-invoice-grid {
        :deep(.ag-header) {
            background-color: #fff !important;
        }
    }

    :deep(.no-data) {
        display: inline-flex !important;
        font-size: 16px;
        display: block;
        padding: 10px;
        justify-content: center;
        color: #a8a8a8;
        align-items: center;
        width: 100%;
        padding-top: 5px;
    }

    :deep(.card-header) {
        background-color: #0d47a1 !important;
        color: #fff;
        padding: 10px !important;
        font-weight: bold;
        align-items: center;
        justify-content: left !important;
        min-height: 50px;
        font-size: 18px;
        padding-left: 15px !important;
    }

    :deep(.reservation-deposit-container) {
        align-items: center;
        display: flex;
        justify-content: right;
    }

    :deep(.reservation-deposit) {
        font-size: 16px !important;
        font-weight: normal !important;

        .edit-link-container {
            background-color: #fff;
            border-radius: 3px;
            padding: 3px 6px;
            align-items: center;
            display: inline-flex;
            font-weight: bold !important;
        }
    }

    :deep(.card-body) {
        padding: 0 !important;
    }

    .property-title-container {
        display: inline-flex;
        align-items: center;
    }

    .property-thumbnail {
        width: 50px;
        height: 50px;
        border-radius: 100%;
        margin-right: 15px;
    }

    .property-name {
        font-size: 18px;
        font-weight: bold;
    }

    @include media-breakpoint-down(md) {
        :deep(.reservation-deposit-container) {
            align-items: center;
            display: flex;
            justify-content: left;
            margin-top: 5px;
        }
    }

    @include media-breakpoint-down(sm) {
        .property-thumbnail {
            width: 30px;
            height: 30px;
            margin-right: 10px;
        }

        .reservation-deposit {
            font-size: 14px !important;
        }

        .property-name {
            font-size: 16px;
        }
    }
</style>
